import { useEffect, useState } from 'react';
import { apiGet, apiPatch } from "../../services/api";
import { useNotification } from '../../contexts/NotificationContext';
import '../styles/AdminPageBuilder.css';
import { useParams } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import AddBlockForm from '../components/AddBlockForm';
import SortableItem from '../components/SortableItem';
import BlockRenderer from '../components/BlockRenderer';

// Функция для получения текста на нужном языке
const getTextByLanguage = (textObj, preferredLang = 'kz') => {
  if (!textObj || typeof textObj !== 'object') return textObj || '';

  return textObj[preferredLang] ||
    textObj.kz ||
    textObj.ru ||
    textObj.en ||
    Object.values(textObj)[0] ||
    '';
};

// Главный компонент страницы
const AdminPageBuilder = () => {
  const [pageData, setPageData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentLanguage, setCurrentLanguage] = useState('kz'); // Язык для просмотра
  const { showNotification } = useNotification();

  const { slug } = useParams();

  const languages = [
    { code: "kz", name: "Қазақша" },
    { code: "ru", name: "Русский" },
    { code: "en", name: "English" }
  ];

  // Настройка сенсоров для drag-n-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadPage();
  }, [slug]);

  const loadPage = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await apiGet(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/block/page/${slug}/full`);
      setPageData(res.data);
    } catch (error) {
      console.error("Ошибка загрузки страницы:", error);
      setError(`Ошибка загрузки страницы: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockAdded = () => {
    loadPage();
    setShowAddForm(false);
  };

  const handleBlockUpdated = (updatedBlock) => {
    if (!pageData || !pageData.blocks) return;

    const updatedBlocks = pageData.blocks.map(block =>
      block.id === updatedBlock.id ? updatedBlock : block
    );

    setPageData({
      ...pageData,
      blocks: updatedBlocks
    });
  };

  const handleBlockDeleted = (deletedBlockId) => {
    if (!pageData || !pageData.blocks) return;

    const updatedBlocks = pageData.blocks.filter(block => block.id !== deletedBlockId);

    setPageData({
      ...pageData,
      blocks: updatedBlocks
    });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!active || !over || active.id === over.id) {
      return;
    }

    const oldIndex = pageData.blocks.findIndex(block => block.id === active.id);
    const newIndex = pageData.blocks.findIndex(block => block.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedBlocks = arrayMove(pageData.blocks, oldIndex, newIndex);
      const updatedBlocks = [...reorderedBlocks];

      setPageData({
        ...pageData,
        blocks: updatedBlocks
      });

      try {
        const updatePromises = updatedBlocks.map((block, index) => {
          if (block.order !== index + 1) {
            return apiPatch(`${process.env.REACT_APP_API_URL}/api/block/${block.id}`, {
              order: index + 1
            },
              );
          }
          return Promise.resolve();
        });

        await Promise.all(updatePromises);

        const finalUpdatedBlocks = updatedBlocks.map((block, index) => ({
          ...block,
          order: index + 1
        }));

        setPageData({
          ...pageData,
          blocks: finalUpdatedBlocks
        });
        showNotification("Порядок блоков обновлен", "success");
      } catch (error) {
        console.error("Ошибка при обновлении порядка блоков:", error);
        showNotification("Ошибка при обновлении порядка", "error");
        loadPage();
      }
    }
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!pageData) {
    return <div className="not-found">Страница не найдена</div>;
  }

  const sortedBlocks = pageData.blocks ? [...pageData.blocks].sort((a, b) => a.order - b.order) : [];

  return (
    <div className="page-builder">
      <header className="page-header">
        <div className="page-title-section">
          <h1>{getTextByLanguage(pageData.titles, currentLanguage)}</h1>

          {/* Переключатель языков для просмотра */}
          <div className="language-selector">
            <label>Язык просмотра:</label>
            <div className="language-tabs">
              {languages.map(lang => (
                <button
                  key={lang.code}
                  type="button"
                  className={`language-tab ${currentLanguage === lang.code ? 'active' : ''}`}
                  onClick={() => setCurrentLanguage(lang.code)}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="page-actions">
          <button
            className="edit-button"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? 'Просмотр' : 'Редактировать'}
          </button>
          {isEditing && (
            <button
              className="add-button"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? 'Скрыть форму' : 'Добавить блок'}
            </button>
          )}
        </div>
      </header>

      {isEditing && showAddForm && (
        <AddBlockForm
          pageId={pageData.id}
          onBlockAdded={handleBlockAdded}
        />
      )}

      <div className="blocks-container">
        {sortedBlocks.length > 0 ? (
          isEditing ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortedBlocks.map(block => block.id)}
                strategy={verticalListSortingStrategy}
              >
                {sortedBlocks.map((block) => (
                  <SortableItem
                    key={block.id}
                    block={block}
                    isEditing={isEditing}
                    currentLanguage={currentLanguage}
                    onBlockUpdated={handleBlockUpdated}
                    onBlockDeleted={handleBlockDeleted}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            sortedBlocks.map((block) => (
              <div key={block.id} className="block-wrapper">
                <BlockRenderer block={block} language={currentLanguage} />
              </div>
            ))
          )
        ) : (
          <div className="empty-page">
            <p>На странице пока нет блоков. Добавьте первый блок!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPageBuilder;